import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, AlertCircleIcon, ShieldIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
export const StudentSignUpFormSection = () => {
  const navigate = useNavigate();
  const {
    registerStudent
  } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [humanVerified, setHumanVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  // PHASE 3: Real-time field validation
  const [fieldErrors, setFieldErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: 'Too Short',
    color: 'bg-gray-300'
  });
  // PHASE 3: Email availability check state
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null);
  useEffect(() => {
    setIsVisible(true);
  }, []);
  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(formData.password));
  }, [formData.password]);
  // PHASE 3: Real-time email validation with debounce
  useEffect(() => {
    if (!formData.email) {
      setEmailAvailable(null);
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setEmailAvailable(null);
      return;
    }
    // Debounce email check
    const timeoutId = setTimeout(async () => {
      setEmailChecking(true);
      try {
        // Check if email exists in Firebase Auth
        // Note: This requires a Cloud Function or backend endpoint
        // For now, we'll skip this check
        setEmailAvailable(true);
      } catch (error) {
        console.error('Error checking email:', error);
      } finally {
        setEmailChecking(false);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.email]);
  const calculatePasswordStrength = (password: string) => {
    if (!password || password.length < 1) {
      return {
        score: 0,
        label: 'Too Short',
        color: 'bg-gray-300'
      };
    }
    let score = 0;
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    if (/[a-z]/.test(password)) score += 0.5;
    if (/[A-Z]/.test(password)) score += 0.5;
    if (/[0-9]/.test(password)) score += 1;
    if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(password)) score += 1;
    score = Math.min(4, score);
    const strengthMap = [{
      score: 0,
      label: 'Too Short',
      color: 'bg-gray-300'
    }, {
      score: 1,
      label: 'Weak',
      color: 'bg-red-500'
    }, {
      score: 2,
      label: 'Fair',
      color: 'bg-orange-500'
    }, {
      score: 3,
      label: 'Good',
      color: 'bg-yellow-500'
    }, {
      score: 4,
      label: 'Strong',
      color: 'bg-green-500'
    }];
    return strengthMap[Math.floor(score)];
  };
  // PHASE 3: Real-time field validation
  const validateField = (fieldName: string, value: string): string => {
    switch (fieldName) {
      case 'firstName':
        if (!value.trim()) return 'First name is required';
        if (value.trim().length < 2) return 'First name must be at least 2 characters';
        return '';
      case 'lastName':
        if (!value.trim()) return 'Last name is required';
        if (value.trim().length < 2) return 'Last name must be at least 2 characters';
        return '';
      case 'email':
        if (!value) return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Please enter a valid email address';
        return '';
      case 'password':
        if (!value) return 'Password is required';
        if (value.length < 8) return 'Password must be at least 8 characters';
        if (!/[0-9]/.test(value)) return 'Password must include at least one number';
        if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(value)) return 'Password must include at least one special character';
        return '';
      case 'confirmPassword':
        if (!value) return 'Please confirm your password';
        if (value !== formData.password) return 'Passwords do not match';
        return '';
      default:
        return '';
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      id,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
    // PHASE 3: Real-time validation
    const error = validateField(id, value);
    setFieldErrors(prev => ({
      ...prev,
      [id]: error
    }));
    setErrorMessage('');
  };
  const validateForm = () => {
    const errors = {
      firstName: validateField('firstName', formData.firstName),
      lastName: validateField('lastName', formData.lastName),
      email: validateField('email', formData.email),
      password: validateField('password', formData.password),
      confirmPassword: validateField('confirmPassword', formData.confirmPassword)
    };
    setFieldErrors(errors);
    // Check if any errors exist
    const hasErrors = Object.values(errors).some(error => error !== '');
    if (hasErrors) {
      setErrorMessage('Please fix the errors above');
      return false;
    }
    if (!agreeToTerms) {
      setErrorMessage('You must agree to the terms and conditions');
      return false;
    }
    if (!humanVerified) {
      setErrorMessage('Please verify that you are human');
      return false;
    }
    return true;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      console.log('Starting student registration process...');
      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      await registerStudent(formData.email, formData.password, formData.firstName, formData.lastName);
      console.log('Student registration successful!');
      setSuccessMessage('Your account has been created! A verification email has been sent to your inbox.');
      localStorage.setItem('studentEmail', formData.email);
      localStorage.setItem('studentName', fullName);
      localStorage.setItem('studentFirstName', formData.firstName);
      localStorage.setItem('studentLastName', formData.lastName);
      await new Promise(resolve => setTimeout(resolve, 1500));
      navigate('/email-verification');
    } catch (error: any) {
      console.error('Error during signup:', error);
      setErrorMessage(error.message || 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const simulateHumanVerification = () => {
    setHumanVerified(true);
  };
  return <section id="signup-form" className="py-16 bg-navy-900/5" data-id="element-11641">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-11642">
        <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} data-id="element-11643">
          <div className="bg-white dark:bg-slate-800 rounded-2xl overflow-hidden shadow-lg border border-gray-100 dark:border-slate-700 p-8" data-id="element-11644">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-navy-900 dark:text-white mb-6 text-center" data-id="element-11645">
              Create Your Student Account
            </h2>
            {successMessage && <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg flex items-start" data-id="element-11646">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-11647" />
                <span data-id="element-11648">{successMessage}</span>
              </div>}
            {errorMessage && <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg flex items-start" data-id="element-11649">
                <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-11650" />
                <span data-id="element-11651">{errorMessage}</span>
              </div>}
            <form onSubmit={handleSubmit} data-id="element-11652">
              <div className="space-y-6" data-id="element-11653">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-11654">
                  {/* First Name */}
                  <div data-id="element-11655">
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-11656">
                      First Name
                    </label>
                    <input type="text" id="firstName" value={formData.firstName} onChange={handleInputChange} className={`w-full px-4 py-3 border rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900 dark:text-white dark:bg-slate-700 ${fieldErrors.firstName ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-slate-600'}`} placeholder="Your first name" required disabled={isSubmitting} data-id="element-11657" />
                    {fieldErrors.firstName && <p className="text-red-500 text-xs mt-1" data-id="element-11658">
                        {fieldErrors.firstName}
                      </p>}
                  </div>
                  {/* Last Name */}
                  <div data-id="element-11659">
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-11660">
                      Last Name
                    </label>
                    <input type="text" id="lastName" value={formData.lastName} onChange={handleInputChange} className={`w-full px-4 py-3 border rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900 dark:text-white dark:bg-slate-700 ${fieldErrors.lastName ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-slate-600'}`} placeholder="Your last name" required disabled={isSubmitting} data-id="element-11661" />
                    {fieldErrors.lastName && <p className="text-red-500 text-xs mt-1" data-id="element-11662">
                        {fieldErrors.lastName}
                      </p>}
                  </div>
                </div>
                {/* Email */}
                <div data-id="element-11663">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-11664">
                    Email Address
                  </label>
                  <div className="relative" data-id="element-11665">
                    <input type="email" id="email" value={formData.email} onChange={handleInputChange} className={`w-full px-4 py-3 border rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900 dark:text-white dark:bg-slate-700 ${fieldErrors.email ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-slate-600'}`} placeholder="you@example.com" required disabled={isSubmitting} data-id="element-11666" />
                    {emailChecking && <div className="absolute right-3 top-3" data-id="element-11667">
                        <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-11668">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-11669"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-11670"></path>
                        </svg>
                      </div>}
                  </div>
                  {fieldErrors.email && <p className="text-red-500 text-xs mt-1" data-id="element-11671">
                      {fieldErrors.email}
                    </p>}
                </div>
                {/* Password */}
                <div data-id="element-11672">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-11673">
                    Password
                  </label>
                  <div className="relative" data-id="element-11674">
                    <input type={passwordVisible ? 'text' : 'password'} id="password" value={formData.password} onChange={handleInputChange} className={`w-full px-4 py-3 border rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900 dark:text-white dark:bg-slate-700 ${fieldErrors.password ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-slate-600'}`} placeholder="Create a secure password" required disabled={isSubmitting} data-id="element-11675" />
                    <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" onClick={() => setPasswordVisible(!passwordVisible)} disabled={isSubmitting} data-id="element-11676">
                      {passwordVisible ? <EyeOffIcon className="h-5 w-5" data-id="element-11677" /> : <EyeIcon className="h-5 w-5" data-id="element-11678" />}
                    </button>
                  </div>
                  {fieldErrors.password && <p className="text-red-500 text-xs mt-1" data-id="element-11679">
                      {fieldErrors.password}
                    </p>}
                  {/* Password Strength Indicator */}
                  {formData.password && <div className="mt-2" data-id="element-11680">
                      <div className="w-full h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden" data-id="element-11681">
                        <div className={`h-full ${passwordStrength.color} transition-all duration-300`} style={{
                      width: `${passwordStrength.score / 4 * 100}%`
                    }} data-id="element-11682"></div>
                      </div>
                      <div className="mt-1 flex justify-between items-center" data-id="element-11683">
                        <span className={`text-xs font-medium ${passwordStrength.score <= 1 ? 'text-red-500' : passwordStrength.score === 2 ? 'text-orange-500' : passwordStrength.score === 3 ? 'text-yellow-500' : 'text-green-500'}`} data-id="element-11684">
                          {passwordStrength.label}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-11685">
                          8+ chars, number, special character
                        </span>
                      </div>
                    </div>}
                </div>
                {/* Confirm Password */}
                <div data-id="element-11686">
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-11687">
                    Confirm Password
                  </label>
                  <div className="relative" data-id="element-11688">
                    <input type={confirmPasswordVisible ? 'text' : 'password'} id="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange} className={`w-full px-4 py-3 border rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900 dark:text-white dark:bg-slate-700 ${fieldErrors.confirmPassword ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-slate-600'}`} placeholder="Confirm your password" required disabled={isSubmitting} data-id="element-11689" />
                    <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)} disabled={isSubmitting} data-id="element-11690">
                      {confirmPasswordVisible ? <EyeOffIcon className="h-5 w-5" data-id="element-11691" /> : <EyeIcon className="h-5 w-5" data-id="element-11692" />}
                    </button>
                  </div>
                  {fieldErrors.confirmPassword && <p className="text-red-500 text-xs mt-1" data-id="element-11693">
                      {fieldErrors.confirmPassword}
                    </p>}
                </div>
                {/* Human Verification */}
                <div data-id="element-11694">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-11695">
                    Human Verification
                  </label>
                  <div className={`border ${humanVerified ? 'border-green-300 bg-green-50 dark:bg-green-900/20 dark:border-green-800' : 'border-gray-300 dark:border-slate-600'} rounded-lg p-4 flex flex-col items-center justify-center cursor-pointer`} onClick={simulateHumanVerification} data-id="element-11696">
                    {humanVerified ? <div className="flex items-center text-green-600 dark:text-green-400" data-id="element-11697">
                        <CheckCircleIcon className="h-5 w-5 mr-2" data-id="element-11698" />
                        <span data-id="element-11699">Verification Complete</span>
                      </div> : <div className="flex flex-col items-center" data-id="element-11700">
                        <ShieldIcon className="h-8 w-8 text-gray-400 mb-2" data-id="element-11701" />
                        <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-11702">
                          Click to verify you're human
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-500 mt-1" data-id="element-11703">
                          (This simulates a CAPTCHA)
                        </span>
                      </div>}
                  </div>
                </div>
                {/* Terms Checkbox */}
                <div className="flex items-start" data-id="element-11704">
                  <div className="flex items-center h-5" data-id="element-11705">
                    <input id="terms" name="terms" type="checkbox" checked={agreeToTerms} onChange={() => setAgreeToTerms(!agreeToTerms)} className="h-4 w-4 text-deep-pink focus:ring-deep-pink border-gray-300 dark:border-slate-600 rounded" required disabled={isSubmitting} data-id="element-11706" />
                  </div>
                  <div className="ml-3 text-sm" data-id="element-11707">
                    <label htmlFor="terms" className="font-medium text-gray-700 dark:text-gray-300" data-id="element-11708">
                      I agree to the{' '}
                      <a href="#" className="text-deep-pink hover:text-burgundy" data-id="element-11709">
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a href="#" className="text-deep-pink hover:text-burgundy" data-id="element-11710">
                        Privacy Policy
                      </a>
                    </label>
                  </div>
                </div>
                {/* Submit Button */}
                <button type="submit" disabled={isSubmitting} className="w-full bg-deep-pink hover:bg-burgundy text-white font-medium py-3 px-4 rounded-lg transition-colors flex justify-center items-center disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-11711">
                  {isSubmitting ? <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-11712">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-11713"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-11714"></path>
                      </svg>
                      Creating Account...
                    </> : 'Create Account'}
                </button>
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-4" data-id="element-11715">
                  Already have an account?{' '}
                  <button type="button" onClick={() => navigate('/student-login')} className="text-deep-pink hover:text-burgundy" disabled={isSubmitting} data-id="element-11716">
                    Sign In
                  </button>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>;
};